import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, Plus, Minus } from 'lucide-react';
import { addHourBalance } from '../services/api';
import { timeZoneForWorker, todayInZone } from '../lib/workerTime';
import { isComputationStartOffset, COMPUTATION_START_LABEL } from '../lib/hourBalance';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// "Hoy" en la zona del trabajador al que se le hace el ajuste.
const todayStr = (worker) => todayInZone(timeZoneForWorker(worker));

// Modal para que el admin añada un ajuste manual a la bolsa de horas de un
// trabajador. Convención de signo expuesta al usuario:
//   · Sumar (verde) → la empresa le debe esas horas al trabajador.
//   · Restar (rojo) → el trabajador le debe esas horas a la empresa.
// Internamente se traduce a offset > 0 / offset < 0 según la doc API.
const HourBalanceModal = ({ open, worker, onClose, onSubmitted }) => {
  const [direction, setDirection] = useState('credit'); // 'credit' (+) | 'debt' (-)
  // Dos campos separados para horas y minutos. Antes había un único campo con
  // un parser que aceptaba 4 formatos ("1", "1,5", "0:30", "1h") — generaba
  // ambigüedad porque escribir "30" pensando "30 minutos" se interpretaba como
  // 30 HORAS y rompía el saldo. Con dos inputs enteros la entrada es siempre
  // inequívoca: total = horas * 60 + minutos.
  const [hoursInt, setHoursInt] = useState('');
  const [minutesInt, setMinutesInt] = useState('');
  const [date, setDate] = useState(() => todayStr(worker));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setDirection('credit');
      setHoursInt('');
      setMinutesInt('');
      setDate(todayStr(worker));
      setReason('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !worker?.id) return null;

  // Parser estricto de enteros >= 0. Acepta string vacío (lo trata como 0)
  // para que el admin pueda dejar uno de los dos campos en blanco sin que
  // explote la validación.
  const parseIntNonNeg = (s) => {
    if (s == null || s === '') return 0;
    const n = Number(String(s).trim());
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return NaN;
    return n;
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault?.();
    setError(null);
    const hh = parseIntNonNeg(hoursInt);
    const mm = parseIntNonNeg(minutesInt);
    if (!Number.isFinite(hh)) { setError('Las horas tienen que ser un número entero ≥ 0.'); return; }
    if (!Number.isFinite(mm)) { setError('Los minutos tienen que ser un número entero ≥ 0.'); return; }
    if (mm > 59) { setError('Los minutos no pueden ser mayores que 59. Si quieres más, súmalos a las horas.'); return; }
    const totalMinutes = hh * 60 + mm;
    if (totalMinutes <= 0) {
      setError('Introduce una cantidad de tiempo mayor que 0 (al menos 1 minuto).');
      return;
    }
    if (!date) {
      setError('La fecha es obligatoria.');
      return;
    }
    if (!reason.trim()) {
      setError('Indica un motivo para el ajuste — queda como traza para auditoría.');
      return;
    }
    // "Inicio de computo" está RESERVADO: esas filas son el marcador de la
    // fecha de alta del trabajador (offset 0, ver src/lib/hourBalance.js) y se
    // excluyen del total. Un ajuste real con ese motivo no se sumaría nunca.
    if (isComputationStartOffset({ reason: reason.trim() })) {
      setError(`"${COMPUTATION_START_LABEL}" es un motivo reservado (marca la fecha de alta del trabajador). Usa otro texto.`);
      return;
    }
    // El backend espera el offset en HORAS (FLOAT). Convertimos desde el
    // total en minutos exacto a horas con división simple. No usamos
    // redondeos intermedios para preservar minutos no múltiplos de 60.
    const hoursFloat = totalMinutes / 60;
    const offset = direction === 'credit' ? hoursFloat : -hoursFloat;
    setSubmitting(true);
    try {
      const res = await addHourBalance({
        workerId: worker.id,
        date,
        reason: reason.trim(),
        offset,
      });
      toast.success(FEEDBACK.hourBalanceAdjusted({
        minutes: totalMinutes, credit: direction === 'credit', dayKey: date, workerName: worker.name,
      }));
      onSubmitted?.(res);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo guardar el ajuste.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <form onSubmit={handleSubmit} className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Bolsa de horas</div>
            <h2 className="font-display text-lg font-bold text-ink">Ajuste manual</h2>
            <div className="text-xs text-ink-muted mt-0.5 truncate">A {worker.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-md transition" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Dirección — botones grandes, son la decisión más importante */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Dirección del ajuste</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDirection('credit')}
                className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-semibold transition ${
                  direction === 'credit'
                    ? 'border-success bg-success/10 text-success'
                    : 'border-slate-200 text-ink-muted hover:text-ink'
                }`}
              >
                <Plus size={14} /> Sumar (a favor del trabajador)
              </button>
              <button
                type="button"
                onClick={() => setDirection('debt')}
                className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-semibold transition ${
                  direction === 'debt'
                    ? 'border-danger bg-danger/10 text-danger'
                    : 'border-slate-200 text-ink-muted hover:text-ink'
                }`}
              >
                <Minus size={14} /> Restar (a favor de la empresa)
              </button>
            </div>
            <p className="text-[11px] text-ink-muted mt-2">
              {direction === 'credit'
                ? 'La empresa le debe estas horas al trabajador. Las podrá recuperar en el futuro.'
                : 'El trabajador le debe estas horas a la empresa. Las tiene que devolver trabajando más.'}
            </p>
          </div>

          {/* Cantidad — dos campos separados para que la entrada sea
              inequívoca. Horas y minutos van enteros; los minutos topan
              en 59 (el navegador refuerza con min/max y el handler valida). */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">
              Cantidad de tiempo
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="number"
                  value={hoursInt}
                  onChange={(e) => setHoursInt(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary tabular-nums"
                  autoFocus
                />
                <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mt-1 text-center">Horas</div>
              </div>
              <div>
                <input
                  type="number"
                  value={minutesInt}
                  onChange={(e) => setMinutesInt(e.target.value)}
                  placeholder="0"
                  min="0"
                  max="59"
                  step="1"
                  inputMode="numeric"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary tabular-nums"
                />
                <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mt-1 text-center">Minutos (0–59)</div>
              </div>
            </div>
            <p className="text-[11px] text-ink-muted mt-2">
              Puedes dejar uno de los dos campos vacío (cuenta como 0). Ejemplos: <b>1h 30m</b> · <b>0h 45m</b> · <b>2h 0m</b>.
            </p>
          </div>

          {/* Fecha */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Fecha del ajuste</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              required
            />
          </div>

          {/* Motivo */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">
              Motivo <span className="text-danger normal-case">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ej. Compensación por horas extras del 5 de mayo · Ajuste tras revisión de fichajes · Devolución de adelanto de horas"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
              required
            />
            <p className="text-[11px] text-ink-muted mt-1">Queda registrado como auditoría — describe brevemente por qué se hace este ajuste.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-danger/5 border border-danger/30 rounded-lg p-3 text-sm">
              <AlertTriangle size={16} className="text-danger flex-shrink-0 mt-0.5" />
              <span className="text-danger">{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink rounded-lg transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60 ${
              direction === 'credit' ? 'bg-success hover:bg-emerald-700' : 'bg-danger hover:bg-red-700'
            }`}
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Guardar ajuste
          </button>
        </div>
      </form>
    </div>
  );
};

export default HourBalanceModal;
